import * as Plot from '@observablehq/plot';
import type { CustomExperimentSpec, PanelRow } from '@learnforge/demo-render/harness';
import { defineExperiment } from '@learnforge/demo-render/harness';
import { createNumberLine } from '../render/number-line';
import { createChartCard } from '../render/chart-card';
import { plotBoxLabeled } from '../render/box-plot';
import { createDataTable, type DataTableStat } from '@learnforge/demo-kit';
import {
  mean,
  median,
  range,
  quartiles,
  iqr,
  tukeyOutliers,
  plotBinThresholds,
  axisTicks,
} from '../statistics';
import { formatNumber } from '@learnforge/demo-math/numeric';
import type { StatsContext, DemoKey } from '../types';

// ---------------------------------------------------------------------------
// Starting shape presets
// ---------------------------------------------------------------------------

type ShapeKey = 'empty' | 'symmetric' | 'right-skewed' | 'bimodal' | 'with-outliers';

const SHAPES: Record<ShapeKey, readonly number[]> = {
  empty: [],
  symmetric: [35, 40, 45, 48, 52, 55, 60, 65],
  'right-skewed': [10, 15, 18, 20, 22, 25, 28, 30, 60, 85],
  bimodal: [20, 22, 25, 28, 70, 72, 75, 78],
  'with-outliers': [40, 42, 44, 46, 48, 50, 52, 54, 100],
};

const SHAPE_OPTIONS: readonly ShapeKey[] = [
  'symmetric',
  'right-skewed',
  'bimodal',
  'with-outliers',
  'empty',
];

const SHAPE_LABEL_KEYS: Record<ShapeKey, DemoKey> = {
  symmetric: 'shape.symmetric',
  'right-skewed': 'shape.right-skewed',
  bimodal: 'shape.bimodal',
  'with-outliers': 'shape.with-outliers',
  empty: 'shape.empty',
};

// ---------------------------------------------------------------------------
// State — model only; DOM refs are transient closure vars in createRender
// ---------------------------------------------------------------------------

interface State {
  currentPoints: number[];
  shape: ShapeKey;
  showMean: boolean;
  showMedian: boolean;
  bins: number;
}

// ---------------------------------------------------------------------------
// Spec factory
// ---------------------------------------------------------------------------

function buildSpec(): CustomExperimentSpec<State, DemoKey, StatsContext> {
  return {
    labelKey: 'exp.distribution-sculptor.title',
    demoId: 'statistics',
    advancedKey: 'distribution-sculptor',

    initialState(): State {
      return {
        currentPoints: [...SHAPES['symmetric']],
        shape: 'symmetric',
        showMean: false,
        showMedian: false,
        bins: 10,
      };
    },

    panel: (state): PanelRow<DemoKey, State>[] => [
      {
        kind: 'dropdown',
        id: 'ds-shape',
        labelKey: 'param.shape.label',
        options: SHAPE_OPTIONS.map((k) => ({ value: k, labelKey: SHAPE_LABEL_KEYS[k] })),
        value: state.shape,
        onChange: (v) => (prev) => ({
          ...prev,
          shape: v as ShapeKey,
          currentPoints: [...SHAPES[v as ShapeKey]],
        }),
      },
      {
        kind: 'toggle',
        labelKey: 'param.show-mean.label',
        value: state.showMean,
        onChange: (v) => (prev) => ({ ...prev, showMean: v }),
      },
      {
        kind: 'toggle',
        labelKey: 'param.show-median.label',
        value: state.showMedian,
        onChange: (v) => (prev) => ({ ...prev, showMedian: v }),
      },
      {
        kind: 'slider',
        id: 'ds-bins',
        labelKey: 'param.bins.label',
        min: 5,
        max: 30,
        step: 1,
        value: state.bins,
        format: (v) => String(v),
        onChange: (v) => (prev) => ({ ...prev, bins: v }),
      },
    ],

    createRender(ctx, host, controls) {
      // --- Layout: one responsive grid; number line is the full-width first row ---
      const grid = document.createElement('div');
      grid.className = 'stats-grid';
      host.appendChild(grid);

      const nlWrap = document.createElement('div');
      nlWrap.className = 'stats-grid-full';
      grid.appendChild(nlWrap);

      // Track the last points array the widget reported so we can guard
      // against fighting an active drag when syncing from state.
      let lastDragPoints: number[] = [...SHAPES['symmetric']];

      const numberLine = createNumberLine({
        container: nlWrap,
        min: 0,
        max: 110,
        initial: [...SHAPES['symmetric']],
        height: 140,
        onChange(pts) {
          lastDragPoints = pts;
          controls.setState((s) => ({ ...s, currentPoints: pts }));
        },
      });

      const legend = document.createElement('div');
      legend.className = 'ds-legend';
      legend.textContent = ctx.i18n.t('legend.points');
      nlWrap.appendChild(legend);

      // `getStats()`/`getRows()` below read these on every table refresh;
      // `update()` keeps them in sync with the live points before calling
      // `dataTable.update()` (mirrors survey-explorer's currentColumnKey guard).
      let currentPts: number[] = [];
      let currentRows: { value: number }[] = [];

      const dataTable = createDataTable<DemoKey>({
        i18n: ctx.i18n,
        columns: [
          {
            id: 'value',
            labelKey: 'table.value',
            format: (v) => formatNumber(v as number, { decimals: 2, trimIntegers: true }),
          },
        ],
        getRows: () => currentRows,
        getStats: () => computeStats(currentPts, ctx.i18n),
        rowNumbers: true,
      });

      const cards = {
        table: createChartCard<DemoKey>({
          titleKey: 'table.data',
          i18n: ctx.i18n,
          headerActions: [dataTable.controls],
          collapsible: {
            storageKey: 'learnforge:collapsible:statistics:distribution-sculptor:table',
          },
        }),
        hist: createChartCard<DemoKey>({
          titleKey: 'card.histogram',
          i18n: ctx.i18n,
          collapsible: {
            storageKey: 'learnforge:collapsible:statistics:distribution-sculptor:hist',
          },
        }),
        box: createChartCard<DemoKey>({
          titleKey: 'card.box',
          i18n: ctx.i18n,
          collapsible: {
            storageKey: 'learnforge:collapsible:statistics:distribution-sculptor:box',
          },
        }),
      };
      grid.append(cards.table.element, cards.hist.element, cards.box.element);
      cards.table.setBody(dataTable.element);

      // --- update: reconcile view to state ---
      const update = (state: State): void => {
        const pts = state.currentPoints;

        currentPts = pts;
        currentRows = [...pts].sort((a, b) => a - b).map((value) => ({ value }));
        dataTable.update();

        // Histogram — x ticks sit on the bin edges (thinned above 12) so the
        // axis lines up with the bars instead of Plot's automatic "nice"
        // ticks; degenerate (empty) edge lists fall back to default ticks.
        const edges = plotBinThresholds(pts, state.bins);
        const histMarks: Plot.Markish[] = [
          Plot.frame({ anchor: 'left' }),
          Plot.rectY(
            pts,
            Plot.binX({ y: 'count' as const }, { x: (d: number) => d, thresholds: edges }),
          ),
          Plot.ruleY([0]),
        ];
        if (state.showMean && pts.length > 0) {
          const mv = mean(pts);
          histMarks.push(Plot.ruleX([mv], { stroke: 'var(--accent-tertiary)', strokeWidth: 2 }));
        }
        if (state.showMedian && pts.length > 0) {
          const mdv = median(pts);
          histMarks.push(
            Plot.ruleX([mdv], { stroke: 'steelblue', strokeWidth: 2, strokeDasharray: '4,3' }),
          );
        }
        cards.hist.setBody(
          Plot.plot({
            x:
              edges.length > 0
                ? {
                    ticks: axisTicks(edges),
                    tickFormat: (d: number) => formatNumber(d, { decimals: 1, trimIntegers: true }),
                  }
                : {},
            marks: histMarks,
            marginBottom: 30,
            height: 200,
          }) as HTMLElement,
        );

        // Box plot
        if (pts.length === 0) {
          const empty = document.createElement('div');
          empty.className = 'empty-card-explainer';
          empty.textContent = ctx.i18n.t('message.add-points');
          cards.box.setBody(empty);
        } else {
          cards.box.setBody(plotBoxLabeled(pts));
        }

        // Sync number-line widget when points changed from a non-drag source
        // (e.g. shape preset). Guard: only call setPoints when the state's
        // currentPoints array differs from what the widget last reported, so we
        // never fight an active drag.
        if (state.currentPoints !== lastDragPoints) {
          lastDragPoints = state.currentPoints;
          numberLine.setPoints(state.currentPoints);
        }
      };

      return {
        update,
        dispose() {
          numberLine.destroy();
          dataTable.destroy();
          for (const c of Object.values(cards)) {
            c.destroy();
          }
        },
      };
    },

    applyShotState(state): State {
      return state;
    },
  };
}

// ---------------------------------------------------------------------------
// Harness experiment + backwards-compat alias
// ---------------------------------------------------------------------------

export const experiment = defineExperiment(buildSpec());

/** Backwards-compat alias so existing registry and tests keep working. */
export const mountDistributionSculptor = experiment.mount.bind(experiment);

// ---------------------------------------------------------------------------
// Data-table stats popover — n, mean, median, range, Q1, Q3, IQR, outlier values.
// ---------------------------------------------------------------------------

function computeStats(pts: readonly number[], i18n: StatsContext['i18n']): DataTableStat[] {
  if (pts.length === 0) {
    return [
      { label: i18n.t('stat.n'), value: '—' },
      { label: i18n.t('stat.mean'), value: '—' },
      { label: i18n.t('stat.median'), value: '—' },
      { label: i18n.t('stat.range'), value: '—' },
      { label: i18n.t('stat.q1'), value: '—' },
      { label: i18n.t('stat.q3'), value: '—' },
      { label: i18n.t('stat.iqr'), value: '—' },
      { label: i18n.t('stat.outliers'), value: '—' },
    ];
  }

  const mv = mean(pts);
  const mdv = median(pts);
  const rv = range(pts);
  const qv = quartiles(pts);
  const iqrVal = iqr(pts);
  const outliers = tukeyOutliers(pts);
  const outlierValue =
    outliers.outlierIndices.length > 0
      ? outliers.outlierIndices
          .map((i) => formatNumber(pts[i]!, { decimals: 2, trimIntegers: true }))
          .join(', ')
      : '—';

  return [
    { label: i18n.t('stat.n'), value: String(pts.length) },
    { label: i18n.t('stat.mean'), value: formatNumber(mv, { decimals: 2, trimIntegers: true }) },
    {
      label: i18n.t('stat.median'),
      value: formatNumber(mdv, { decimals: 2, trimIntegers: true }),
    },
    {
      label: i18n.t('stat.range'),
      value: formatNumber(rv.span, { decimals: 2, trimIntegers: true }),
    },
    { label: i18n.t('stat.q1'), value: formatNumber(qv.q1, { decimals: 2, trimIntegers: true }) },
    { label: i18n.t('stat.q3'), value: formatNumber(qv.q3, { decimals: 2, trimIntegers: true }) },
    {
      label: i18n.t('stat.iqr'),
      value: formatNumber(iqrVal, { decimals: 2, trimIntegers: true }),
    },
    { label: i18n.t('stat.outliers'), value: outlierValue },
  ];
}
